import React from "react";
import {
  Box,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import {
  AddOutlined,
  DeleteOutlined,
  EditOutlined,
  SmartToyOutlined,
  SearchOutlined,
} from "@mui/icons-material";
import { GlobalContext } from "../../context/GlobalContext";
import CommonDialog from "../../common/CommonDialog";
import PageHeader from "../../common/PageHeader";

const DOMAINS = ["Image", "Video", "Sound"];
const STATUSES = ["active", "inactive"];
const ASPECT_USAGES = ["All", "Image", "Video", "Sound"];

const emptyResolution = { name: "", resolution: "", status: "active" };
const emptyFeature = { domain: "Video", featureName: "", status: "active" };
const emptyAspect = { name: "", ratio: "", usage: "All", status: "active" };
const emptyModel = {
  displayName: "",
  modelId: "",
  domain: "Video",
  featureId: "",
  providerId: "",
  fallbackModelId: "",
  resolutionIds: [],
  aspectRatioId: "",
  maxResolution: "",
  maxDuration: 0,
  creditsPerGeneration: 0,
  providerCost: 0,
  noInputGeneration: 0,
  enabled: true,
};

function StatCard({ label, value }) {
  const theme = useTheme();
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: alpha(theme.palette.primary.main, 0.03),
      }}
    >
      <Typography variant="caption" color="text.secondary" fontWeight={700}>
        {label}
      </Typography>
      <Typography variant="h5" fontWeight={800}>
        {value}
      </Typography>
    </Paper>
  );
}

export default function ModelRegistry({ lang }) {
  const { hitAxios } = React.useContext(GlobalContext);

  const [tab, setTab] = React.useState(0);
  const [stats, setStats] = React.useState({
    totalModels: 0,
    activeModels: 0,
    features: 0,
    aspectRatios: 0,
  });
  const [models, setModels] = React.useState([]);
  const [features, setFeatures] = React.useState([]);
  const [resolutions, setResolutions] = React.useState([]);
  const [aspectRatios, setAspectRatios] = React.useState([]);
  const [providers, setProviders] = React.useState([]);
  const [filters, setFilters] = React.useState({ q: "", domain: "", featureId: "" });

  const [dialog, setDialog] = React.useState({ open: false, type: null, id: null });
  const [form, setForm] = React.useState({});
  const [saving, setSaving] = React.useState(false);

  const api = React.useCallback(
    async (path, { post = false, obj, showSnackbar = true } = {}) => {
      const res = await hitAxios({
        path,
        post,
        obj,
        admin: true,
        showLoading: false,
        showSnackbar,
      });
      return res?.data;
    },
    [hitAxios],
  );

  const loadLookups = React.useCallback(async () => {
    const [feat, res, aspect, prov] = await Promise.all([
      api("/api/beyund-ai-registry/features", { showSnackbar: false }),
      api("/api/beyund-ai-registry/resolutions", { showSnackbar: false }),
      api("/api/beyund-ai-registry/aspect-ratios", { showSnackbar: false }),
      api("/api/beyund-ai-provider/list", { showSnackbar: false }),
    ]);
    if (feat?.success) setFeatures(feat.data || []);
    if (res?.success) setResolutions(res.data || []);
    if (aspect?.success) setAspectRatios(aspect.data || []);
    if (prov?.success) setProviders(prov.data || []);
  }, [api]);

  const loadModels = React.useCallback(
    async (nextFilters = filters) => {
      const params = new URLSearchParams();
      if (nextFilters.q) params.set("q", nextFilters.q);
      if (nextFilters.domain) params.set("domain", nextFilters.domain);
      if (nextFilters.featureId) params.set("featureId", nextFilters.featureId);
      const qs = params.toString();
      const [list, st] = await Promise.all([
        api(`/api/beyund-ai-registry/models${qs ? `?${qs}` : ""}`, { showSnackbar: false }),
        api("/api/beyund-ai-registry/models/stats", { showSnackbar: false }),
      ]);
      if (list?.success) setModels(list.data || []);
      if (st?.success) setStats(st.data);
    },
    [api, filters],
  );

  React.useEffect(() => {
    loadLookups();
    loadModels();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openAdd = (type) => {
    const base =
      type === "model"
        ? emptyModel
        : type === "feature"
          ? emptyFeature
          : type === "aspect"
            ? emptyAspect
            : emptyResolution;
    setForm({ ...base });
    setDialog({ open: true, type, id: null });
  };

  const openEdit = (type, row) => {
    if (type === "model") {
      setForm({
        ...emptyModel,
        ...row,
        featureId: row.featureId || "",
        providerId: row.providerId || "",
        fallbackModelId: row.fallbackModelId || "",
        aspectRatioId: row.aspectRatioId || "",
        resolutionIds: row.resolutionIds || [],
        enabled: Boolean(row.enabled),
      });
    } else {
      setForm({ ...(type === "feature" ? emptyFeature : type === "aspect" ? emptyAspect : emptyResolution), ...row });
    }
    setDialog({ open: true, type, id: row.id });
  };

  const closeDialog = () => setDialog({ open: false, type: null, id: null });

  const handleSave = async () => {
    setSaving(true);
    try {
      const isEdit = Boolean(dialog.id);
      const map = {
        model: ["models", { ...form, id: dialog.id || undefined, featureId: form.featureId || null, providerId: form.providerId || null, fallbackModelId: form.fallbackModelId || null, aspectRatioId: form.aspectRatioId || null }],
        feature: ["features", { ...form, id: dialog.id || undefined }],
        resolution: ["resolutions", { ...form, id: dialog.id || undefined }],
        aspect: ["aspect-ratios", { ...form, id: dialog.id || undefined }],
      };
      const [resource, obj] = map[dialog.type];
      const path = `/api/beyund-ai-registry/${resource}/${isEdit ? "update" : "add"}`;
      const res = await api(path, { post: true, obj });
      if (res?.success) {
        closeDialog();
        await loadLookups();
        await loadModels();
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm("Delete this item?")) return;
    const resource =
      type === "model"
        ? "models"
        : type === "feature"
          ? "features"
          : type === "aspect"
            ? "aspect-ratios"
            : "resolutions";
    const res = await api(`/api/beyund-ai-registry/${resource}/delete`, {
      post: true,
      obj: { id },
    });
    if (res?.success) {
      await loadLookups();
      await loadModels();
    }
  };

  const toggleResolutionId = (id) => {
    setForm((prev) => {
      const current = Array.isArray(prev.resolutionIds) ? prev.resolutionIds : [];
      return {
        ...prev,
        resolutionIds: current.includes(id)
          ? current.filter((x) => x !== id)
          : [...current, id],
      };
    });
  };

  const filteredFeatures = features.filter((f) => !form.domain || f.domain === form.domain);
  const activeAspects = aspectRatios.filter((a) => a.status === "active");
  const activeResolutions = resolutions.filter((r) => r.status === "active");

  const dialogTitle = {
    model: dialog.id ? "Edit Model" : "Add Model",
    feature: dialog.id ? "Edit Feature" : "Add Feature",
    resolution: dialog.id ? "Edit Resolution" : "Add Resolution",
    aspect: dialog.id ? "Edit Aspect Ratio" : "Add Aspect Ratio",
  }[dialog.type];

  const addLabel =
    tab === 0 ? "Add Model" : tab === 1 ? "Add Feature" : tab === 2 ? "Add Resolution" : "Add Aspect Ratio";
  const addType = tab === 0 ? "model" : tab === 1 ? "feature" : tab === 2 ? "resolution" : "aspect";

  return (
    <Box>
      <PageHeader
        title={lang?.modelRegistry || "Model Registry"}
        subtitle="Enable, configure, and price AI models for Image, Video, and Sound."
        icon={SmartToyOutlined}
        primaryAction={
          <Button variant="contained" startIcon={<AddOutlined />} onClick={() => openAdd(addType)}>
            {addLabel}
          </Button>
        }
      />

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} md={3}>
          <StatCard label="Total Models" value={stats.totalModels} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label="Active" value={stats.activeModels} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label="Features" value={stats.features} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label="Aspect Ratios" value={stats.aspectRatios} />
        </Grid>
      </Grid>

      <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ borderBottom: "1px solid", borderColor: "divider", px: 1 }}
        >
          <Tab label="Model Registry" />
          <Tab label="Feature" />
          <Tab label="Resolution" />
          <Tab label="Aspect Ratios" />
        </Tabs>

        {tab === 0 && (
          <Box sx={{ p: 2 }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1.5}
              sx={{ mb: 2 }}
              alignItems={{ md: "center" }}
            >
              <TextField
                size="small"
                placeholder="Search models..."
                value={filters.q}
                onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                InputProps={{
                  startAdornment: <SearchOutlined fontSize="small" sx={{ mr: 1 }} />,
                }}
                sx={{ minWidth: 220 }}
              />
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Domain</InputLabel>
                <Select
                  label="Domain"
                  value={filters.domain}
                  onChange={(e) => setFilters((f) => ({ ...f, domain: e.target.value }))}
                >
                  <MenuItem value="">All Domains</MenuItem>
                  {DOMAINS.map((d) => (
                    <MenuItem key={d} value={d}>
                      {d}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Feature</InputLabel>
                <Select
                  label="Feature"
                  value={filters.featureId}
                  onChange={(e) => setFilters((f) => ({ ...f, featureId: e.target.value }))}
                >
                  <MenuItem value="">All Features</MenuItem>
                  {features.map((f) => (
                    <MenuItem key={f.id} value={f.id}>
                      {f.featureName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button variant="contained" onClick={() => loadModels()}>
                Apply
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  const cleared = { q: "", domain: "", featureId: "" };
                  setFilters(cleared);
                  loadModels(cleared);
                }}
              >
                Clear
              </Button>
            </Stack>

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Model Name</TableCell>
                  <TableCell>Domain</TableCell>
                  <TableCell>Provider / Model ID</TableCell>
                  <TableCell>Feature</TableCell>
                  <TableCell>Credits</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {models.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <Typography fontWeight={600}>{row.displayName}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={row.domain} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{row.provider?.name || "—"}</Typography>
                      <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                        {row.modelId}
                      </Typography>
                    </TableCell>
                    <TableCell>{row.feature?.featureName || "—"}</TableCell>
                    <TableCell>{row.creditsPerGeneration}</TableCell>
                    <TableCell>{row.maxDuration ? `${row.maxDuration}s` : "—"}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={row.enabled ? "success" : "default"}
                        label={row.enabled ? "Active" : "Inactive"}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => openEdit("model", row)}>
                        <EditOutlined fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete("model", row.id)}>
                        <DeleteOutlined fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {!models.length && (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Typography color="text.secondary" textAlign="center" py={4}>
                        No models yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        )}

        {tab === 1 && (
          <Box sx={{ p: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Feature</TableCell>
                  <TableCell>Domain</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {features.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <Typography fontWeight={600}>{row.featureName}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={row.domain} />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={row.status === "active" ? "success" : "default"}
                        label={row.status}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => openEdit("feature", row)}>
                        <EditOutlined fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete("feature", row.id)}>
                        <DeleteOutlined fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {!features.length && (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography color="text.secondary" textAlign="center" py={4}>
                        No features yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        )}

        {tab === 2 && (
          <Box sx={{ p: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Resolution</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {resolutions.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <Typography fontWeight={600}>{row.name}</Typography>
                    </TableCell>
                    <TableCell>{row.resolution}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={row.status === "active" ? "success" : "default"}
                        label={row.status}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => openEdit("resolution", row)}>
                        <EditOutlined fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete("resolution", row.id)}
                      >
                        <DeleteOutlined fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {!resolutions.length && (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography color="text.secondary" textAlign="center" py={4}>
                        No resolutions yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        )}

        {tab === 3 && (
          <Box sx={{ p: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Ratio</TableCell>
                  <TableCell>Usage</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {aspectRatios.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <Typography fontWeight={600}>{row.name}</Typography>
                    </TableCell>
                    <TableCell>{row.ratio}</TableCell>
                    <TableCell>{row.usage}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={row.status === "active" ? "success" : "default"}
                        label={row.status}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => openEdit("aspect", row)}>
                        <EditOutlined fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete("aspect", row.id)}>
                        <DeleteOutlined fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {!aspectRatios.length && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography color="text.secondary" textAlign="center" py={4}>
                        No aspect ratios yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        )}
      </Paper>

      <CommonDialog
        open={dialog.open}
        onClose={() => !saving && closeDialog()}
        title={dialogTitle}
        icon={dialog.id ? EditOutlined : AddOutlined}
        maxWidth="md"
        fullWidth
      >
        <Stack spacing={2} sx={{ pt: 1 }}>
          {dialog.type === "model" && (
            <>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Display Name"
                    placeholder="e.g. Kling V3.0"
                    value={form.displayName || ""}
                    onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Provider Model ID"
                    placeholder="e.g. kwaivgi/kling-v3"
                    value={form.modelId || ""}
                    onChange={(e) => setForm((f) => ({ ...f, modelId: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Domain</InputLabel>
                    <Select
                      label="Domain"
                      value={form.domain || "Video"}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, domain: e.target.value, featureId: "" }))
                      }
                    >
                      {DOMAINS.map((d) => (
                        <MenuItem key={d} value={d}>
                          {d}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Feature</InputLabel>
                    <Select
                      label="Feature"
                      value={form.featureId || ""}
                      onChange={(e) => setForm((f) => ({ ...f, featureId: e.target.value }))}
                    >
                      <MenuItem value="">None</MenuItem>
                      {filteredFeatures.map((f) => (
                        <MenuItem key={f.id} value={f.id}>
                          {f.featureName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Provider</InputLabel>
                    <Select
                      label="Provider"
                      value={form.providerId || ""}
                      onChange={(e) => setForm((f) => ({ ...f, providerId: e.target.value }))}
                    >
                      <MenuItem value="">Select provider</MenuItem>
                      {providers.map((p) => (
                        <MenuItem key={p.id} value={p.id}>
                          {p.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Fallback Model</InputLabel>
                    <Select
                      label="Fallback Model"
                      value={form.fallbackModelId || ""}
                      onChange={(e) => setForm((f) => ({ ...f, fallbackModelId: e.target.value }))}
                    >
                      <MenuItem value="">None</MenuItem>
                      {models
                        .filter((m) => m.id !== dialog.id)
                        .map((m) => (
                          <MenuItem key={m.id} value={m.id}>
                            {m.displayName}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Aspect Ratio</InputLabel>
                    <Select
                      label="Aspect Ratio"
                      value={form.aspectRatioId || ""}
                      onChange={(e) => setForm((f) => ({ ...f, aspectRatioId: e.target.value }))}
                    >
                      <MenuItem value="">None</MenuItem>
                      {activeAspects.map((a) => (
                        <MenuItem key={a.id} value={a.id}>
                          {a.name} ({a.ratio})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {(form.domain === "Video" || form.domain === "Sound") && (
                <Box>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                    Resolutions
                  </Typography>
                  <Stack spacing={0.5}>
                    {activeResolutions.map((r) => (
                      <FormControlLabel
                        key={r.id}
                        control={
                          <Switch
                            checked={(form.resolutionIds || []).includes(r.id)}
                            onChange={() => toggleResolutionId(r.id)}
                          />
                        }
                        label={`${r.name} (${r.resolution})`}
                      />
                    ))}
                    {!activeResolutions.length && (
                      <Typography variant="body2" color="text.secondary">
                        No resolutions configured yet. Add some in the Resolution tab.
                      </Typography>
                    )}
                  </Stack>
                </Box>
              )}

              <Grid container spacing={2}>
                {(form.domain === "Video" || form.domain === "Sound") && (
                  <>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Max Resolution"
                        value={form.maxResolution || ""}
                        onChange={(e) => setForm((f) => ({ ...f, maxResolution: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Max Duration (sec)"
                        value={form.maxDuration ?? 0}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, maxDuration: Number(e.target.value) }))
                        }
                      />
                    </Grid>
                  </>
                )}
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Credits / Generation"
                    value={form.creditsPerGeneration ?? 0}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, creditsPerGeneration: Number(e.target.value) }))
                    }
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Provider Cost ($)"
                    value={form.providerCost ?? 0}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, providerCost: Number(e.target.value) }))
                    }
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="No Input/Generation"
                    value={form.noInputGeneration ?? 0}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, noInputGeneration: Number(e.target.value) }))
                    }
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Enabled</InputLabel>
                    <Select
                      label="Enabled"
                      value={form.enabled ? "Yes" : "No"}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, enabled: e.target.value === "Yes" }))
                      }
                    >
                      <MenuItem value="Yes">Yes</MenuItem>
                      <MenuItem value="No">No</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </>
          )}

          {dialog.type === "feature" && (
            <>
              <TextField
                fullWidth
                label="Feature Name"
                value={form.featureName || ""}
                onChange={(e) => setForm((f) => ({ ...f, featureName: e.target.value }))}
              />
              <FormControl fullWidth>
                <InputLabel>Domain</InputLabel>
                <Select
                  label="Domain"
                  value={form.domain || "Video"}
                  onChange={(e) => setForm((f) => ({ ...f, domain: e.target.value }))}
                >
                  {DOMAINS.map((d) => (
                    <MenuItem key={d} value={d}>
                      {d}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={form.status || "active"}
                  onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                >
                  {STATUSES.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}

          {dialog.type === "resolution" && (
            <>
              <TextField
                fullWidth
                label="Name"
                placeholder="e.g. 1080p"
                value={form.name || ""}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
              <TextField
                fullWidth
                label="Resolution"
                placeholder="e.g. 1920x1080"
                value={form.resolution || ""}
                onChange={(e) => setForm((f) => ({ ...f, resolution: e.target.value }))}
              />
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={form.status || "active"}
                  onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                >
                  {STATUSES.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}

          {dialog.type === "aspect" && (
            <>
              <TextField
                fullWidth
                label="Name"
                placeholder="e.g. Landscape"
                value={form.name || ""}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
              <TextField
                fullWidth
                label="Ratio"
                placeholder="e.g. 16:9"
                value={form.ratio || ""}
                onChange={(e) => setForm((f) => ({ ...f, ratio: e.target.value }))}
              />
              <FormControl fullWidth>
                <InputLabel>Usage</InputLabel>
                <Select
                  label="Usage"
                  value={form.usage || "All"}
                  onChange={(e) => setForm((f) => ({ ...f, usage: e.target.value }))}
                >
                  {ASPECT_USAGES.map((u) => (
                    <MenuItem key={u} value={u}>
                      {u}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={form.status || "active"}
                  onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                >
                  {STATUSES.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}

          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button onClick={closeDialog} disabled={saving}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {dialog.id ? "Save Changes" : "Add"}
            </Button>
          </Stack>
        </Stack>
      </CommonDialog>
    </Box>
  );
}
